<template>
    <div
        ref="rootEl"
        class="modal-container modal-container-corner modal-container-corner-notifications no-transition"
        :class="{ hidden: !display }"
        tabindex="-1"
        role="dialog"
        :aria-hidden="!display"
        @click="close"
        @keydown="escapeToClose"
    >
        <div class="modal-dialog modal-md account-settings-dd notifications-dd" role="document" @click="stopPropagationEvent">
            <header class="notifications-dd-header">
                <h3 class="notifications-dd-title">{{ t("Notifications") }}</h3>
                <button type="button" class="notifications-dd-close" :title="t('Close')" @click="close">
                    <i class="fas fa-times" aria-hidden="true"></i>
                </button>
            </header>

            <div class="account-settings-dd-body">
                <div v-if="loading" class="notifications-dd-empty">{{ t("Loading...") }}</div>
                <div v-else-if="notifications.length === 0" class="notifications-dd-empty">
                    {{ t("You have no new notifications") }}
                </div>
                <div v-else class="account-settings-dd-actions notifications-dd-list">
                    <button
                        v-for="notification in notifications"
                        :key="notification.id"
                        type="button"
                        class="settings-dd-link notifications-dd-item"
                        :class="{ 'notifications-dd-item--unread': !notification.read }"
                        :disabled="markingId === notification.id"
                        @click="openNotification(notification)"
                    >
                        <span class="settings-dd-icon" aria-hidden="true">
                            <i class="fas" :class="getNotificationIcon(notification.type)"></i>
                        </span>
                        <span class="settings-dd-link-text">
                            <span>{{ getNotificationTitle(notification.type) }}</span>
                            <span class="settings-dd-link-meta notifications-dd-message">
                                {{ getNotificationDescription(notification.type, notification.info) }}
                            </span>
                            <span class="notifications-dd-date">{{ formatDate(notification.date) }}</span>
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import { NotificationsController, type NotificationItem } from "@/control/notifications";
import { useAppEventListener } from "@/composables/useAppEventListener";
import { useDropdownPanel } from "@/composables/useDropdownPanel";
import { getNotificationDescription, getNotificationIcon, getNotificationLink, getNotificationTitle } from "@/utils/notifications-display";

defineOptions({ name: "NotificationsDropdown" });

const display = defineModel<boolean>("display");

const router = useRouter();
const rootEl = useTemplateRef<HTMLElement>("rootEl");
const notifications = ref(NotificationsController.GetPreviewNotifications());
const loading = ref(false);
const markingId = ref<string | null>(null);

const refreshNotifications = () => {
    notifications.value = NotificationsController.GetPreviewNotifications();
};

const { close, escapeToClose, stopPropagationEvent } = useDropdownPanel(display, rootEl, {
    focusTrapExceptClass: "top-bar-notifications-button",
    onActivate: () => {
        loading.value = true;
        NotificationsController.LoadPreview(10, () => {
            refreshNotifications();
            loading.value = false;
        });
    },
});

const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
};

const openNotification = (notification: NotificationItem) => {
    if (markingId.value) {
        return;
    }

    const go = () => {
        close();
        router.push(getNotificationLink(notification.type, notification.info));
    };

    if (notification.read) {
        go();
        return;
    }

    markingId.value = notification.id;
    NotificationsController.MarkRead(notification.id, () => {
        markingId.value = null;
        refreshNotifications();
        go();
    });
};

useAppEventListener("notifications-preview-changed", refreshNotifications);
</script>
